// export default Catalogue;
import { Link, useLocation } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import games from "../../data/games";
import { Search, Clock, Target, Users, Cpu, SlidersHorizontal, Sparkles } from "lucide-react";

function Catalogue() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const prePack = params.get("packType");
  const preSessions = params.get("sessions");

   useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 👇 On affiche les jeux beta + preview
  const baseGames = games.filter((g) => g.isBeta === true || g.isPreview === true);
  const [search, setSearch] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("all");

  const difficultyOptions = useMemo(
    () => Array.from(new Set(baseGames.map((g) => g.difficulty).filter(Boolean))),
    [baseGames]
  );

  const filteredGames = baseGames.filter((g) => {
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!`${g.name ?? ""} ${g.description ?? ""}`.toLowerCase().includes(q)) return false;
    }
    if (difficultyFilter !== "all" && g.difficulty !== difficultyFilter) return false;
    return true;
  });

  const difficultyColor = {
    Facile: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10",
    Intermédiaire: "text-amber-400 border-amber-400/30 bg-amber-400/10",
    Avancé: "text-red-400 border-red-400/30 bg-red-400/10",
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Hero sombre ─────────────────────────────────────────────── */}
      <div className="relative overflow-hidden bg-slate-900">
        <div className="pointer-events-none absolute inset-0 opacity-[0.035]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")", backgroundSize: "120px" }} />
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at 15% 50%, rgba(99,102,241,0.14) 0, transparent 55%), radial-gradient(ellipse at 85% 20%, rgba(249,115,22,0.09) 0, transparent 50%)" }} />
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)", backgroundSize: "64px 64px" }} />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/80" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-18">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-indigo-400 uppercase mb-4">
              Catalogue
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-3">
              Catalogue des expériences
            </h1>
            <p className="text-sm sm:text-base text-slate-400 leading-relaxed">
              Des escape games digitaux pensés pour les entreprises. Collaboration, immersion et communication.
            </p>
          </div>
        </div>
      </div>

      {/* ── Filtres ─────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative flex-1 w-full">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom du jeu, mots-clés…"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none"
              />
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <SlidersHorizontal className="h-4 w-4 text-slate-400" />
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => setDifficultyFilter("all")}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                    difficultyFilter === "all"
                      ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                      : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"
                  }`}
                >
                  Toutes
                </button>
                {difficultyOptions.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDifficultyFilter(d)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                      difficultyFilter === d
                        ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                        : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>

            <p className="text-[11px] text-slate-400 flex-shrink-0">
              {filteredGames.length} expérience{filteredGames.length > 1 ? "s" : ""}
            </p>
          </div>
        </div>
      </div>

      {/* ── Grid des jeux ───────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {filteredGames.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-400 text-sm">Aucun jeu ne correspond à vos critères.</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {filteredGames.map((g) =>
              g.isPreview ? <PreviewCard key={g.id} g={g} /> : <GameCard key={g.id} g={g} prePack={prePack} preSessions={preSessions} difficultyColor={difficultyColor} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Card jeu disponible (existant) ────────────────────────────────
function GameCard({ g, prePack, preSessions, difficultyColor }) {
  return (
    <article className="group relative flex flex-col overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-indigo-200">
      <div className="pointer-events-none absolute inset-0 z-10 opacity-0 group-hover:opacity-100 transition duration-300 rounded-2xl" style={{ background: "radial-gradient(circle at 20% 0%, rgba(99,102,241,0.08) 0, transparent 60%)" }} />

      <div className="relative overflow-hidden">
        <img src={g.image} alt={g.name} className="w-full h-52 object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />

        <div className="absolute top-3 left-3 flex gap-2 z-10">
          {g.isAI && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-900/80 border border-white/10 backdrop-blur px-2.5 py-1 text-[11px] font-medium text-white">
              <Cpu className="h-3 w-3 text-orange-300" />IA
            </span>
          )}
        </div>

        {g.tags?.length > 0 && (
          <div className="absolute bottom-3 left-3 flex flex-wrap gap-1.5 z-10">
            {g.tags.slice(0, 2).map(tag => (
              <span key={tag} className="inline-flex items-center rounded-full bg-black/50 border border-white/15 backdrop-blur px-2 py-0.5 text-[10px] font-medium text-white/80">
                # {tag}
              </span>
            ))}
          </div>
        )}

        {g.difficulty && (
          <span className={`absolute top-3 right-3 z-10 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold backdrop-blur ${difficultyColor[g.difficulty] || "text-slate-300 border-white/20 bg-white/10"}`}>
            <Target className="h-3 w-3" />{g.difficulty}
          </span>
        )}
      </div>

      <div className="relative flex flex-col flex-1 p-5">
        <h2 className="text-lg font-bold text-slate-900 mb-1.5 group-hover:text-indigo-700 transition-colors">{g.name}</h2>
        <p className="text-sm text-slate-500 line-clamp-4 leading-relaxed mb-4">{g.description}</p>

        <div className="flex flex-wrap gap-2 mt-auto mb-4">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
            <Clock className="h-3.5 w-3.5 text-slate-400" />{g.duration}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-xs text-indigo-700 font-medium">
            <Users className="h-3.5 w-3.5 text-indigo-400" />{g.players}
          </span>
        </div>

        <Link
          to={`/catalogue/${g.id}?packType=${prePack || "normal"}&sessions=${preSessions || 1}`}
          className="group/cta inline-flex w-full items-center justify-center rounded-full bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition relative overflow-hidden"
        >
          <span className="pointer-events-none absolute inset-0 opacity-0 group-hover/cta:opacity-100 transition" style={{ background: "radial-gradient(circle at 30% 0%, rgba(249,115,22,0.3) 0, transparent 55%)" }} />
          <span className="relative">Découvrir et réserver →</span>
        </Link>
      </div>
    </article>
  );
}

// ─── Card jeu preview (nouveau) ────────────────────────────────────
function PreviewCard({ g }) {
  return (
    <article className="group relative flex flex-col overflow-hidden rounded-2xl bg-white border-2 border-dashed border-indigo-200 shadow-sm transition-all duration-300 hover:shadow-lg">
      {/* Image grisée légèrement */}
      <div className="relative overflow-hidden">
        <img src={g.image} alt={g.name} className="w-full h-52 object-cover" style={{ filter: "saturate(0.7) contrast(0.95)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/30 to-transparent" />

        {/* Badge "Bientôt disponible" */}
        <div className="absolute top-3 left-3 z-10">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 px-3 py-1.5 text-[11px] font-bold text-white shadow-lg">
            <Sparkles className="h-3 w-3" />
            BIENTÔT DISPONIBLE
          </span>
        </div>

        {/* Date de sortie cible */}
        {g.releaseTarget && (
          <div className="absolute top-3 right-3 z-10">
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 border border-white/15 backdrop-blur px-2.5 py-1 text-[11px] font-semibold text-white">
              {g.releaseTarget}
            </span>
          </div>
        )}

        {/* Tags bas */}
        {g.tags?.length > 0 && (
          <div className="absolute bottom-3 left-3 flex flex-wrap gap-1.5 z-10">
            {g.tags.slice(0, 2).map(tag => (
              <span key={tag} className="inline-flex items-center rounded-full bg-black/50 border border-white/15 backdrop-blur px-2 py-0.5 text-[10px] font-medium text-white/80">
                # {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="relative flex flex-col flex-1 p-5 bg-gradient-to-br from-white to-indigo-50/40">
        <h2 className="text-lg font-bold text-slate-900 mb-1.5">{g.name}</h2>
        <p className="text-sm text-slate-600 line-clamp-4 leading-relaxed mb-4">{g.description}</p>

        <div className="flex flex-wrap gap-2 mt-auto mb-4">
          {g.duration && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
              <Clock className="h-3.5 w-3.5 text-slate-400" />{g.duration}
            </span>
          )}
          {g.players && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-xs text-indigo-700 font-medium">
              <Users className="h-3.5 w-3.5 text-indigo-400" />{g.players}
            </span>
          )}
          {g.difficulty && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs text-emerald-700 font-medium">
              <Target className="h-3.5 w-3.5 text-emerald-400" />{g.difficulty}
            </span>
          )}
        </div>

        {/* CTA disabled mais visible */}
        <div className="inline-flex w-full items-center justify-center rounded-full bg-slate-100 border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-500 cursor-not-allowed">
          🔔 En cours de développement
        </div>
      </div>
    </article>
  );
}

export default Catalogue;